import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Warehouse, HardHat, ShieldCheck, MapPinned } from 'lucide-react';
import { useSettings } from '@/context/SettingsContext';
import { motion } from 'framer-motion';

const iconMap = {
  Warehouse,
  Building2,
  HardHat,
  ShieldCheck,
  MapPinned,
};

export const Home = () => {
  const { settings, loading } = useSettings();
  const videoRef = useRef(null);

  // Force video to play on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(err => {
        console.log('Video autoplay failed:', err);
      });
    }
  }, [settings?.hero?.videoUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-600">Cargando...</div>
      </div>
    );
  }

  const hero = settings?.hero || {};
  const stats = settings?.company?.stats || [];
  const services = settings?.services || [];
  const company = settings?.company || {};
  const primaryColor = settings?.theme?.primaryColor || '#1e3a5f';
  const about = settings?.about || {};
  
  // Default features if none configured
  const defaultFeatures = [
    { icon: 'MapPinned', title: 'Ubicación estratégica', description: 'Seleccionamos y configuramos predios con accesibilidad real: arterias principales, radios de giro, y logística de última milla.' },
    { icon: 'Warehouse', title: 'Flexibilidad', description: 'Proyectos multi-tenant y build-to-suit: módulos, docks, oficinas y expansiones planificadas por etapas.' },
    { icon: 'ShieldCheck', title: 'Calidad y seguridad', description: 'Infraestructura operativa, estándares de seguridad, y un enfoque de mantenimiento para operación 24/7.' }
  ];
  const features = about.features?.length > 0 ? about.features : defaultFeatures;

  return (
    <main>
      {/* Hero con Video Full Screen */}
      <section 
        style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100vh', 
          overflow: 'hidden', 
          backgroundColor: '#1a1a2e' 
        }}
      >
        {/* Video Background - Full Screen */}
        {hero.videoUrl && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              minWidth: '100%',
              minHeight: '100%',
              transform: 'translate(-50%, -50%)',
              objectFit: 'cover'
            }}
          >
            <source src={hero.videoUrl} type="video/mp4" />
          </video>
        )}
        
        {/* Overlay oscuro sobre el video */}
        {hero.videoUrl && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1 }} />
        )}
        
        {/* Fallback gradient when no video */}
        {!hero.videoUrl && (
          <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white" />
        )}
        
        {/* Contenido superpuesto al video */}
        <div 
          className="container-safe" 
          style={{ 
            position: 'relative', 
            zIndex: 2, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            paddingTop: '10vh',
            paddingBottom: '10vh'
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={`mx-auto max-w-3xl ${hero.videoUrl ? 'text-center' : 'text-center'}`}
          >
            <div className={`inline-flex items-center rounded-full border ${hero.videoUrl ? 'border-white/30 bg-white/10 text-white' : 'border-slate-200 bg-white text-slate-600'} px-3 py-1 text-xs font-medium`}>
              {hero.badge}
            </div>
            <h1 className={`mt-4 text-3xl font-semibold tracking-tight ${hero.videoUrl ? 'text-white' : 'text-slate-900'} sm:text-4xl md:text-5xl lg:text-6xl leading-tight`}>
              {hero.title}
            </h1>
            <p className={`mt-5 text-sm leading-relaxed ${hero.videoUrl ? 'text-white/90' : 'text-slate-600'} sm:text-base md:text-lg max-w-2xl mx-auto px-4`}>
              {hero.subtitle}
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/proyectos"
                className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold shadow-soft hover:opacity-95 transition-opacity ${hero.videoUrl ? 'bg-white text-slate-900' : 'text-white'}`}
                style={!hero.videoUrl ? { backgroundColor: primaryColor } : {}}
                data-testid="home-view-projects-btn"
              >
                Conocer proyectos <ArrowRight size={18} />
              </Link>
              <Link
                to="/contacto"
                className={`inline-flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition-colors ${hero.videoUrl ? 'border-white/50 bg-transparent text-white hover:bg-white/10' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`}
                data-testid="home-contact-btn"
              >
                Más información <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          {stats.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12 grid grid-cols-2 gap-3 px-4 sm:gap-4 md:grid-cols-4 w-full max-w-4xl"
            >
              {stats.map((s, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-slate-200 bg-white p-3 sm:p-5 text-center shadow-soft"
                >
                  <div className="text-xl sm:text-2xl font-semibold text-slate-900">{s.value}</div>
                  <div className="mt-1 text-[10px] sm:text-xs font-medium text-slate-600 leading-tight">{s.label}</div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Quiénes Somos */}
      <section id="quienes" className="py-12 sm:py-16 md:py-20">
        <div className="container-safe px-4">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold tracking-widest text-slate-500">QUIÉNES SOMOS</div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
              {about.title || 'Desarrollo de infraestructura estratégica'}
            </h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">
              {about.description || 'Somos una compañía dedicada al desarrollo de parques logísticos y de inmuebles, con una sólida trayectoria en la creación de infraestructura estratégica para operaciones industriales, logísticas y de distribución.'}
            </p>
          </div>

          <div className="mt-8 sm:mt-10 grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-3">
            {features.map((feature, idx) => {
              const Icon = iconMap[feature.icon] || MapPinned;
              return (
                <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-soft">
                  <div className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-slate-100">
                    <Icon size={18} className="sm:w-5 sm:h-5" />
                  </div>
                  <div className="mt-3 sm:mt-4 text-base sm:text-lg font-semibold text-slate-900">{feature.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Soluciones */}
      <section id="soluciones" className="py-12 sm:py-16 md:py-20 bg-slate-50">
        <div className="container-safe px-4">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold tracking-widest text-slate-500">SOLUCIONES</div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
              Nuestras soluciones
            </h2>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">
              Soluciones integrales que se adaptan a cada proyecto y etapa de desarrollo.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2">
            {services.map((s, idx) => {
              const Icon = iconMap[s.icon] || Warehouse;
              return (
                <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-soft">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-slate-100 flex-shrink-0">
                      <Icon size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base sm:text-lg font-semibold text-slate-900">{s.title}</div>
                      <p className="mt-1 sm:mt-2 text-sm leading-relaxed text-slate-600">{s.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/proyectos"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              Explorar proyectos <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};
